<template>
  <page-wrapper>
    <template #title>
      <div class="h3">
        Общие настройки
        <status v-if="accommodationObject" :status="accommodationObject.commonInformation.status" />
        <skeleton-loader v-else width="100px" height="24px" class="mt-lg-1" />
      </div>
    </template>

    <skeleton-loader v-if="!accommodationObject" width="100%" :height="$vuetify.display.smAndDown ? '46px' : '58px'" />

    <template v-else>
      <e-alert v-if="accommodationObject.commonInformation.status === ApproveStatus.Approved" variant="info">
        <div class="paragraph-m_h font-600">
          Для изменения данных объекта размещения обратитесь в
          <router-link :to="{ name: RouteNames.Support }" class="link">Поддержку</router-link>
        </div>
      </e-alert>
      <e-alert v-else-if="accommodationObject.commonInformation.status === ApproveStatus.OnApproval" variant="info">
        <div class="paragraph-m_h font-600">
          После подтверждения данных объекта размещения можно внести корректировки
        </div>
        <div class="paragraph-s_h">
          Если данные будут отличаться от указанных и вы захотите внести корректировки, обратитесь в
          <router-link :to="{ name: RouteNames.Support }" class="link">Поддержку</router-link>.
        </div>
      </e-alert>
    </template>

    <property-block class="mt-4 mt-md-6">
      <template #text>
        <div class="paragraph-l font-600">Общая информация</div>
      </template>
      <template #content>
        <property-item title="Наименование">
          <skeleton-loader
            v-if="!accommodationObject"
            width="260px"
            :height="$vuetify.display.mdAndDown ? '24px' : '25px'"
          />
          <multilanguage-text v-else :text="accommodationObject.commonInformation.name">
            <template #default="{ value }">
              <div class="paragraph-m_h font-600">{{ value }}</div>
            </template>
          </multilanguage-text>
        </property-item>
      </template>
    </property-block>

    <property-block class="mt-4 mt-md-6">
      <template #content>
        <div class="d-flex flex-wrap row-gap-4">
          <property-item title="Тип объекта размещения" class="object-settings__item">
            <skeleton-loader v-if="!accommodationObject" width="200px" type="paragraph-m_h" />
            <template v-else>
              {{ accommodationObject.commonInformation.type }}
            </template>
          </property-item>

          <property-item title="Категория / звёздность" class="object-settings__item">
            <skeleton-loader v-if="!accommodationObject" width="200px" type="paragraph-m_h" />
            <template v-else>
              {{ accommodationObject.commonInformation.category }}
            </template>
          </property-item>

          <property-item title="Город" class="object-settings__item">
            <skeleton-loader v-if="!accommodationObject" width="200px" type="paragraph-m_h" />
            <template v-else>
              {{ accommodationObject.commonInformation.town }}
            </template>
          </property-item>
        </div>
      </template>
    </property-block>
    <divider class="my-6" />

    <property-block>
      <template #text>
        <div class="paragraph-l font-600">Контактные данные</div>
      </template>
      <template #content>
        <div
          v-if="!accommodationObject || accommodationObject?.contacts.phone || accommodationObject?.contacts.email"
          class="d-flex flex-wrap row-gap-4 mb-4 mb-md-6"
        >
          <property-item
            v-if="!accommodationObject || accommodationObject?.contacts.phone"
            title="Телефон"
            class="object-settings__item"
          >
            <skeleton-loader v-if="!accommodationObject" width="200px" type="paragraph-m_h" />

            <phone-number v-else :value="accommodationObject.contacts.phone" />
          </property-item>
          <property-item
            v-if="!accommodationObject || accommodationObject?.contacts.email"
            title="Адрес почты"
            class="object-settings__item"
          >
            <skeleton-loader v-if="!accommodationObject" width="200px" type="paragraph-m_h" />

            <a v-else :href="`mailto:${accommodationObject.contacts.email}`" class="link">
              {{ accommodationObject.contacts.email }}
            </a>
          </property-item>
        </div>
        <property-item
          v-if="!accommodationObject || accommodationObject?.contacts.postalAddress.length"
          title="Адрес "
          class="object-settings__item"
        >
          <skeleton-loader v-if="!accommodationObject" width="100%" type="paragraph-m_h" />

          <multilanguage-text v-else :text="accommodationObject.contacts.postalAddress">
            <template #default="{ value }">
              {{ value }}
            </template>
          </multilanguage-text>
        </property-item>
      </template>
    </property-block>

    <property-block v-if="accommodationObject?.contacts.webSite" class="mt-4 mt-md-6">
      <template #content>
        <property-item title="Адрес сайта" class="object-settings__item">
          <a :href="accommodationObject.contacts.webSite" class="link">{{ accommodationObject.contacts.webSite }}</a>
        </property-item>
      </template>
    </property-block>
  </page-wrapper>
</template>

<script setup lang="ts">
import PhoneNumber from '@/core/components/common/PhoneNumber.vue';
import Divider from '@/core/components/helpers/Divider.vue';
import PageWrapper from '@/core/components/interface/PageWrapper.vue';
import PropertyBlock from '@/core/components/interface/PropertyBlock.vue';
import PropertyItem from '@/core/components/interface/PropertyItem.vue';
import SkeletonLoader from '@/core/components/interface/SkeletonLoader.vue';
import { EAlert, MultilanguageText } from '@/core/components/ui';
import { RouteNames } from '@/core/plugins/router/routeNames';
import { ApproveStatus } from '@/graphql/generated';
import Status from '@/parts/objects/components/Status.vue';
import { useAccommodationObjectsStore } from '@/parts/objects/store/accommodationObjects';

const { accommodationObject } = useAccommodationObjectsStore();
</script>

<style scoped lang="scss">
.object-settings {
  &__item {
    min-width: 240px;

    &:not(:last-child) {
      margin-right: 40px;
    }

    @include md-and-down {
      min-width: initial;
    }
  }
}
</style>
